import * as jsonLogic from 'json-logic-js';

const equalsOperation = function(a, b) { return a === b; };
const notEqualsOperation = function(a, b) { return a !== b; };
const greaterThanOperation = (a, b) => a > b;
const greaterThanOrEqualOperation = (a, b) => a >= b;
const lessThanOperation = (a, b) => a < b;
const lessThanOrEqualOperation = (a, b) => a <= b;
const betweenOperation = (value, min, max) => value >= min && value <= max;
const concatOperation = (...args) => jsonLogic.apply({ cat: args });

const addOperation = (...args) => {
  const nums = args.map(a => {
    const n = Number(a);
    if (isNaN(n)) {
      throw new Error(`Cannot convert "${a}" to a number.`);
    }
    return n;
  });
  return nums.reduce((a, b) => a + b, 0);
};

const subtractOperation = (...args) => {
  const nums = args.map(Number);
  if (nums.length === 0) return 0;
  if (nums.length === 1) return -nums[0];
  return nums.reduce((a, b) => a - b);
};
const multiplyOperation = (...args) => args.reduce((a, b) => Number(a) * Number(b), 1);

const divideOperation = (...args) => {
  const nums = args.map(Number);
  for (let i = 1; i < nums.length; i++) {
    if (nums[i] === 0) {
      throw new Error("Division by zero is not allowed");
    }
  }
  return nums.slice(1).reduce((a, b) => a / b, nums[0]);
};

const ifOperation = function(...args) {
  for (let i = 0; i < args.length - 1; i += 2) {
    if (args[i]) return args[i + 1];
  }
  return args[args.length - 1];
};
const notOperation = (a) => !a;
const startsWithOperation = (str, prefix) => {
  return String(str).startsWith(String(prefix));
};

const containsOperation = (str, substring) => {
  return String(str).includes(String(substring));
};

const doesNotContainOperation = (str, substring) => {
  return !String(str).includes(String(substring));
};

const endsWithOperation = (str, suffix) => {
  return String(str).endsWith(String(suffix));
};

const isEmptyOperation = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

const isNotEmptyOperation = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

const sumOperation = (...args) => {
  const nums = args.map(Number).filter(n => !isNaN(n));
  return nums.reduce((a, b) => a + b, 0);
};

const avgOperation = (...args) => {
  const nums = args.map(Number).filter(n => !isNaN(n));
  return nums.length > 0 ? Math.round(nums.reduce((a, b) => a + b, 0) / nums.length) : 0;
};

const countOperation = (...args) => {
  return args.filter(val => val !== null && val !== undefined).length;
};

const maxOperation = (...args) => {
  const nums = args.map(Number).filter(n => !isNaN(n));
  return nums.length > 0 ? Math.max(...nums): 0;
};

const minOperation = (...args) => {
  const nums = args.map(Number).filter(n => !isNaN(n));
  return nums.length > 0 ? Math.min(...nums) : 0;
};

const dateDiffOperation = (unit, startDate, endDate) => {
  if (!unit || !startDate || !endDate) return null;
  const d1 = new Date(startDate);
  const d2 = new Date(endDate);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return null;
  const diffTime = Math.abs(d2.getTime() - d1.getTime());

  switch (unit.toLowerCase()) {
    case 'days':
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    case 'weeks':
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
    case 'months':
      return Math.abs((d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth()));
    case 'years':
      return Math.abs(d2.getFullYear() - d1.getFullYear());
    default:
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
};

const dateAddOperation = (unit, amount, dateStr) => {
  if (!dateStr) return null;
  let date = typeof dateStr === 'number' ? new Date(dateStr) : new Date(dateStr);
  if (isNaN(date.getTime())) return null;

  amount = Number(amount) || 0;
  unit = (unit || 'days').toLowerCase();

  if (unit === 'days') {
    date.setDate(date.getDate() + amount);
  } else if (unit === 'weeks') {
    date.setDate(date.getDate() + amount * 7);
  } else if (unit === 'months') {
    const origDate = date.getDate();
    date.setMonth(date.getMonth() + amount);

    if (date.getDate() < origDate) {
      date.setDate(0);
    }
  } else if (unit === 'years') {
    const origDate = date.getDate();
    date.setFullYear(date.getFullYear() + amount);

    if (date.getDate() < origDate) {
      date.setDate(0);
    }
  } else {
    date.setDate(date.getDate() + amount);
  }

  return date.toISOString().slice(0, 10);
};

jsonLogic.add_operation('dateadd', dateAddOperation);

const orOperation = (...args) => {
  if (args.length === 0) return false;
  for (let i = 0; i < args.length; i++) {
    if (args[i]) return args[i];
  }
  return args[args.length - 1];
};

const andOperation = (...args) => {
  if (args.length === 0) return true;
  for (let i = 0; i < args.length; i++) {
    if (!args[i]) return args[i];
  }
  return args[args.length - 1];
};

const thenOperation = (condition, trueValue, falseValue = null) => {
  return condition ? trueValue : falseValue;
};

const isNullOperation = (value) => value === null || value === undefined || value === '';
const isNotNullOperation = (value) => value !== null && value !== undefined && value !== '';

 const notAllowedOperations = ["%"];

// additional operation by default: +, -, *, /, <=, >=, !, !!, var, missing, missing_some, map, reduce, filter, all, none, some, merge, in, cat, substr, log
jsonLogic.add_operation("eq", equalsOperation);
jsonLogic.add_operation("equals", equalsOperation); 
jsonLogic.add_operation("=", equalsOperation);
jsonLogic.add_operation("neq", notEqualsOperation);
jsonLogic.add_operation("notequals", notEqualsOperation);
jsonLogic.add_operation("gt", greaterThanOperation);
jsonLogic.add_operation("greaterthan", greaterThanOperation);
jsonLogic.add_operation("gte", greaterThanOrEqualOperation);
jsonLogic.add_operation("greaterthanorequal", greaterThanOrEqualOperation);
jsonLogic.add_operation("lt", lessThanOperation);
jsonLogic.add_operation("lessthan", lessThanOperation);
jsonLogic.add_operation("lte", lessThanOrEqualOperation);
jsonLogic.add_operation("lessthanorequal", lessThanOrEqualOperation);
jsonLogic.add_operation("between", betweenOperation);
jsonLogic.add_operation("concat", concatOperation);
jsonLogic.add_operation("add", addOperation);
jsonLogic.add_operation("+", addOperation);
jsonLogic.add_operation("subtract", subtractOperation);
jsonLogic.add_operation("-", subtractOperation);
jsonLogic.add_operation("multiply", multiplyOperation);
jsonLogic.add_operation("divide", divideOperation);
jsonLogic.add_operation("/", divideOperation);
jsonLogic.add_operation("if", ifOperation);
jsonLogic.add_operation("not", notOperation);
jsonLogic.add_operation("startswith", startsWithOperation);
jsonLogic.add_operation("contains", containsOperation);
jsonLogic.add_operation("doesnotcontain", doesNotContainOperation);
jsonLogic.add_operation("endswith", endsWithOperation);
jsonLogic.add_operation("isempty", isEmptyOperation);
jsonLogic.add_operation("isnotempty", isNotEmptyOperation);
jsonLogic.add_operation("sum", sumOperation);
jsonLogic.add_operation("avg", avgOperation);
jsonLogic.add_operation("count", countOperation);
jsonLogic.add_operation("max", maxOperation);
jsonLogic.add_operation("min", minOperation);
jsonLogic.add_operation("datediff", dateDiffOperation);
jsonLogic.add_operation("dateadd", dateAddOperation);
jsonLogic.add_operation("or", orOperation);
jsonLogic.add_operation("and", andOperation);
jsonLogic.add_operation("then", thenOperation);
jsonLogic.add_operation("isnull", isNullOperation);
jsonLogic.add_operation("isnotnull", isNotNullOperation);

notAllowedOperations.forEach(operation => {
  jsonLogic.add_operation(operation, (...args) => {
    throw new Error("Operation is not allowed: " + operation);
  });
});

export default jsonLogic;